import * as _ from 'lodash-es';
import React, { useLayoutEffect, useRef, useState } from 'react';
import PostTopicListItem from './PostTopicListItem';
import * as styles from './PostTopicWithBreadcrumbs.module.scss';
import useIsPostTopicBarLarge from './useIsPostTopicBarLarge';
import Button from '../../components/Button';
import SVGIcon from '../../components/SVGIcon';
import TextWithIcon from '../../components/TextWithIcon';
import Tooltip from '../../components/Tooltip';
import { isTouchDevice } from '../../constants/os';
import { IS_EXTENSION } from '../../constants/platform';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import clearIcon from '../../icons/non-prefilled/16x16/clear.svg';
import topicIcon from '../../icons/prefilled/16x16/topic.svg';
import TopicBreadcrumbs from '../../topic/TopicBreadcrumbs';
import TopicDetailPreview from '../../topic/TopicDetailPreview';
import TopicNameWithIcon from '../../topic/TopicNameWithIcon';
import getPotentialElementWidth from '../../utils/getPotentialElementWidth';
import Link from '../../utils/router/Link';
import topicPath from '../../utils/url/topicPath';
import useIsPublicView from '../../utils/useIsPublicView';

const TOPIC_FRAGMENT = gql(`
  fragment PostTopicWithBreadcrumbs_topic on TopicFields {
    id
    name
    ancestors {
      ...TopicBreadcrumbs_topics
    }
    ...topicPath_topic
    ...TopicNameWithIcon_topic
  }
`);

type TopicFragment = DocumentType<typeof TOPIC_FRAGMENT>;

interface LinkPopOverContainerProps {
  topicId: string;
  path: string;
  children: React.ReactNode;
  popOverDisabled: boolean;
}

const LinkPopOverContainer = ({
  children,
  path,
  topicId,
  popOverDisabled,
}: LinkPopOverContainerProps) => {
  const [isPopOverHidden, setPopOverHidden] = useState(true);

  if (popOverDisabled) return children;

  return (
    <PostTopicListItem.PopOver
      hidden={isPopOverHidden}
      popOver={
        <TopicDetailPreview
          topicId={topicId}
          setMinHeight={() => setPopOverHidden(false)}
        />
      }
    >
      <Link
        className={styles.link}
        to={path}
        onClick={(event) => {
          if (isTouchDevice) {
            event.preventDefault();
          }
        }}
      >
        {children}
      </Link>
    </PostTopicListItem.PopOver>
  );
};

type PostTopicWithBreadcrumbsProps = {
  topic: TopicFragment;
  itemsPerRow: 1 | 2 | 3;
  containerEl: HTMLUListElement | null;
  className?: string;
} & (
  | { editing?: false; post?: never }
  | {
      editing: true;
      removeDisabledMessage: string | undefined;
      onRemove: (topic: TopicFragment) => unknown;
    }
);

const PostTopicWithBreadcrumbs = ({
  topic,
  containerEl,
  itemsPerRow,
  ...props
}: PostTopicWithBreadcrumbsProps) => {
  const postTopicListItemRef = useRef<HTMLLIElement>(null);
  const topicNameContainerRef = useRef<HTMLDivElement>(null);
  const topicNameRef = useRef<HTMLDivElement>(null);

  const isPublicView = useIsPublicView();
  const hasNoAncestor = _.isEmpty(topic.ancestors);
  const isLarge = useIsPostTopicBarLarge();

  const [sizingParameters, setSizingParameters] = useState<{
    topicNameWidth: number;
    maximumAllowedBreadcrumbsSpace: number;
  } | null>(null);

  useLayoutEffect(() => {
    const topicNameEl = topicNameRef.current;
    const postTopicListItemEl = postTopicListItemRef.current;
    const topicNameContainerEl = topicNameContainerRef.current;

    if (
      !topicNameEl ||
      !containerEl ||
      !postTopicListItemEl ||
      !topicNameContainerEl
    ) {
      return undefined;
    }

    const recalculate = () => {
      const innerContainerRect = postTopicListItemEl.getBoundingClientRect();
      const contentRect = topicNameContainerEl.getBoundingClientRect();

      // we are using this component inside containers that could be scaled down
      // so we need to account for that too
      const scale =
        topicNameEl.getBoundingClientRect().width / topicNameEl.clientWidth;

      // this is to account for the icon and the gap left to the topic name.
      // We also need to unscale the additional width, because it uses
      // the bounding client rect, which could be scaled down. But in CSS, we want to
      // apply the unscaled values so that CSS will apply the scaling correctly.
      const additionalWidth =
        (innerContainerRect.width - contentRect.width) / scale;

      const columnGap = parseInt(getComputedStyle(containerEl).columnGap, 10);
      const topicNameWidth = getPotentialElementWidth(topicNameEl);

      // This accounts for column gap. Per row, we have (n-1) column gaps in between the items
      // and we spread this negative space equally among those items.
      // Example: For 3 items in a row, there would be 2 gaps in between them. When column gap is 24px,
      // we need to account for 48px of negative space and we spread it equally among the 3 items, so
      // that all items are the same size.
      const maximumAllowedSpaceDrivenByBreadcrumbs =
        containerEl.getBoundingClientRect().width / itemsPerRow -
        ((itemsPerRow - 1) * columnGap) / itemsPerRow;

      setSizingParameters({
        maximumAllowedBreadcrumbsSpace: Math.min(
          Math.max(
            maximumAllowedSpaceDrivenByBreadcrumbs - additionalWidth,
            topicNameWidth,
          ),
          containerEl.getBoundingClientRect().width,
        ),
        topicNameWidth: topicNameWidth + additionalWidth,
      });
    };

    recalculate();
    const observer = new ResizeObserver(recalculate);
    observer.observe(containerEl);

    return () => {
      observer.disconnect();
    };
  }, [containerEl, itemsPerRow]);

  const style = sizingParameters
    ? {
        // Using flex basis with the same rule as above instead of directly setting
        // the calculated value will prevent flickering when the user resizes the window.
        // The disadvantage is that we have to rely on the --column-gap to be set from the parent.
        flexBasis: `max(${
          sizingParameters.topicNameWidth
        }px, calc(100% / ${itemsPerRow} - ${
          itemsPerRow - 1
        } * var(--column-gap, 0) / ${itemsPerRow}))`,
      }
    : undefined;

  const icon = props.editing ? (
    <div className={styles.remove}>
      <Tooltip content={props.removeDisabledMessage}>
        <Button
          onClick={(event) => {
            event.preventDefault();
            props.onRemove(topic);
          }}
          block
          disabled={!!props.removeDisabledMessage}
          aria-label="Remove topic from post"
          className={styles.removeButton}
        >
          <SVGIcon>{clearIcon}</SVGIcon>
        </Button>
      </Tooltip>
    </div>
  ) : (
    <SVGIcon role="presentation">{topicIcon}</SVGIcon>
  );

  const topicName = (
    <div className={styles.topicNameContainer} ref={topicNameContainerRef}>
      <div className={styles.topicNameInner}>
        {isPublicView ? (
          <TextWithIcon
            truncate="single"
            text={topic.name}
            to={topicPath(topic)}
            ref={topicNameRef}
          />
        ) : (
          <TopicNameWithIcon
            iconSize="medium"
            topic={topic}
            truncate="single"
            ref={topicNameRef}
          />
        )}
      </div>
    </div>
  );

  const topicBreadcrumbs = !hasNoAncestor && sizingParameters != null && (
    <TopicBreadcrumbs
      link={isPublicView}
      truncate
      topics={topic.ancestors}
      size={isLarge ? 'regular' : 'small'}
      maximumAllowedSpace={sizingParameters.maximumAllowedBreadcrumbsSpace}
    />
  );

  return (
    <PostTopicListItem.Root
      ref={postTopicListItemRef}
      aria-label={`Topic: ${topic.name}`}
      style={style}
    >
      <LinkPopOverContainer
        topicId={topic.id}
        path={topicPath(topic)}
        popOverDisabled={isPublicView || IS_EXTENSION}
      >
        <PostTopicListItem.Content
          icon={icon}
          name={topicName}
          description={topicBreadcrumbs}
        />
      </LinkPopOverContainer>
    </PostTopicListItem.Root>
  );
};

export default PostTopicWithBreadcrumbs;
